import { useState } from "react";
import axios from "axios";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { company } from "@/mock/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you within one business day.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const cards = [
    { icon: MapPin, title: "Visit us", lines: [company.address] },
    { icon: Mail, title: "Email us", lines: [company.email], href: `mailto:${company.email}` },
    { icon: Phone, title: "Call us", lines: [company.phone], href: `tel:${company.phoneHref}` },
    { icon: Clock, title: "Office hours", lines: [company.hours] },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Contact"
        title="Let's talk about protecting your business"
        subtitle="Reach out to our Halifax team — whether you need a SOC, a forensic investigation or simply expert advice, we're here to help."
      />

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-5 gap-10">
          {/* info */}
          <div className="lg:col-span-2 space-y-4">
            {cards.map((c) => (
              <Reveal key={c.title}>
                <div className="flex gap-4 rounded-2xl border border-white/10 bg-brand-navy2/60 p-6">
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-brand-lime/10 text-brand-lime"><c.icon className="h-6 w-6" /></div>
                  <div>
                    <p className="font-display font-semibold text-white">{c.title}</p>
                    {c.lines.map((l) => (
                      c.href
                        ? <a key={l} href={c.href} className="block text-sm text-slate-400 hover:text-brand-lime transition-colors">{l}</a>
                        : <p key={l} className="text-sm text-slate-400">{l}</p>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* form */}
          <Reveal delay={100} className="lg:col-span-3">
            <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-brand-navy2/60 p-8">
              <h2 className="font-display text-2xl font-bold text-white">Send us a message</h2>
              <p className="mt-2 text-sm text-slate-400">Fields marked with * are required.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name">Full name *</Label>
                  <Input id="c-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border-white/10" placeholder="Jane Doe" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-email">Email *</Label>
                  <Input id="c-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-white/5 border-white/10" placeholder="jane@company.ca" />
                </div>
              </div>
              <div className="mt-5 space-y-1.5">
                <Label htmlFor="c-subject">Subject</Label>
                <Input id="c-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="bg-white/5 border-white/10" placeholder="How can we help?" />
              </div>
              <div className="mt-5 space-y-1.5">
                <Label htmlFor="c-msg">Message *</Label>
                <Textarea id="c-msg" value={form.message} onChange={(e) => set("message", e.target.value)} className="bg-white/5 border-white/10 min-h-[140px]" placeholder="Tell us about your requirements..." />
              </div>
              <Button type="submit" disabled={submitting} className="group mt-6 bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold rounded-full px-7 h-12 disabled:opacity-70">
                {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>) : (<>Send message<Send className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></>)}
              </Button>
            </form>
          </Reveal>
        </div>

        <div className="mx-auto max-w-7xl px-6 mt-10">
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <iframe
              title="Halifax office map"
              src="https://www.google.com/maps?q=256+Alabaster+Way,+Halifax,+NS+B3P+0G1&output=embed"
              className="w-full h-[360px]"
              style={{ border: 0, filter: "grayscale(0.3) invert(0.9) hue-rotate(180deg)" }}
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </>
  );
}
